// © Microsoft Corporation. All rights reserved.
import { __decorate } from "tslib";
import { FASTElement, attr, observable, DOM } from "@microsoft/fast-element";
import { isTreeItemElement } from "@microsoft/fast-foundation";
import { keyCodeEscape } from "@microsoft/fast-web-utilities";
export class HamburgerMenu extends FASTElement {
    constructor() {
        super(...arguments);
        /** Flag to indicate if menu should be shown */
        this.isMenuVisible = false;
        /** Hamburger menu data object, which includes the nav items, the selected nav id, and whether to use mobile styles */
        this.navItems = [];
        /** Flattened list of nav items */
        this.flattenedNavItems = [];
        /**
         * Handles when the selected item changes, and emits a selected-change event with the nav item object
         * @param e - event
         */
        this.onSelectedChange = (e) => {
            // Stop propagation of the event so event listeners don't receive multiple selected-change events
            e.stopPropagation();
            if (!e.target || !this.flattenedNavItems.length) {
                return;
            }
            if (!isTreeItemElement(e.target)) {
                return;
            }
            if (!e.target.selected || !e.target.id) {
                return;
            }
            const newSelectedNavId = e.target.id;
            const newSelectedNavItem = this.flattenedNavItems.find(item => item.id === newSelectedNavId);
            if (newSelectedNavItem) {
                this.$emit("change", { selectedNav: newSelectedNavItem });
                this.closeMenu();
            }
        };
        /**
         * Exits hamburger menu on esc
         * @param e - keyboard event
         */
        this.handleDocumentKeydown = (e) => {
            if (e.defaultPrevented) {
                return;
            }
            if (e.keyCode === keyCodeEscape) {
                this.closeMenu();
                return;
            }
        };
        /**
         * Closes hamburger menu when user tabs out
         * @param e - focus event
         */
        this.onMenuFocusout = (e) => {
            const isNestedEl = this.menuRef.contains(e.relatedTarget);
            if (!isNestedEl) {
                this.closeMenu();
            }
        };
        /**
         * Click listener to close menu if user clicks outside the menu
         * @param event - click event
         */
        this.onOverlayClick = (event) => {
            const targetClass = event.target.getAttribute("class");
            if (targetClass && targetClass.includes("overlay")) {
                this.closeMenu();
            }
        };
    }
    /**
     * Connected callback. When component is inserted into the DOM, add proper event listeners.
     */
    connectedCallback() {
        super.connectedCallback();
        this.menuOverlay.addEventListener("click", this.onOverlayClick);
        document.addEventListener("keydown", this.handleDocumentKeydown);
        // We need to wait for one paint so that the slotted tree items will be populated properly
        window.requestAnimationFrame(() => this.setSelectedNav(this.selectedNavId));
    }
    /**
     * Disconnected callback. When component is removed from the DOM, remove event listeners as part of clean up.
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        this.menuOverlay.removeEventListener("click", this.onOverlayClick);
        document.removeEventListener("keydown", this.handleDocumentKeydown);
    }
    /**
     * Opens the hamburger menu
     */
    openMenu() {
        this.isMenuVisible = true;
        if (this.closeButton) {
            DOM.queueUpdate(() => this.closeButton.focus());
        }
    }
    /**
     * Closes the hamburger menu
     */
    closeMenu() {
        this.isMenuVisible = false;
    }
    /**
     * Sets the selected nav item based on the passed in id
     * @param selectedNavId - selected nav id
     */
    setSelectedNav(selectedNavId) {
        if (!this.treeView ||
            !this.treeView.slottedTreeItems ||
            !this.treeView.slottedTreeItems.length) {
            return;
        }
        let newSelectedNavItem;
        for (const treeItem of this.treeView.slottedTreeItems) {
            if (treeItem.id === selectedNavId) {
                newSelectedNavItem = treeItem;
                break;
            }
            if (!treeItem.childItems ||
                !treeItem.childItems.length) {
                continue;
            }
            newSelectedNavItem = treeItem.childItems.find(child => child.id === selectedNavId);
            if (newSelectedNavItem) {
                break;
            }
        }
        if (!newSelectedNavItem && this.treeView.currentSelected) {
            this.treeView.currentSelected.removeAttribute("selected");
            return;
        }
        if (!newSelectedNavItem || newSelectedNavItem === this.treeView.currentSelected) {
            return;
        }
        if (this.treeView.currentSelected) {
            this.treeView.currentSelected.removeAttribute("selected");
        }
        this.treeView.currentSelected = newSelectedNavItem;
        this.treeView.currentSelected.setAttribute("selected", "true");
    }
    navItemsChanged(oldValue, newValue) {
        if (!newValue) {
            return;
        }
        this.flattenedNavItems = [...newValue];
        newValue.forEach(item => {
            if (!item.childItems) {
                return;
            }
            this.flattenedNavItems.push(...item.childItems);
        });
        // Check if the current selected nav is part of the new nav items
        window.requestAnimationFrame(() => this.setSelectedNav(this.selectedNavId));
    }
    selectedNavIdChanged(oldValue, newValue) {
        if (!newValue || newValue === oldValue) {
            return;
        }
        this.setSelectedNav(newValue);
    }
}
__decorate([
    attr({ attribute: "menu-button-tooltip" })
], HamburgerMenu.prototype, "menuButtonTooltip", void 0);
__decorate([
    attr({ attribute: "close-button-tooltip" })
], HamburgerMenu.prototype, "closeButtonTooltip", void 0);
__decorate([
    attr({ attribute: "use-mobile-styles", mode: "boolean" })
], HamburgerMenu.prototype, "useMobileStyles", void 0);
__decorate([
    observable
], HamburgerMenu.prototype, "isMenuVisible", void 0);
__decorate([
    observable
], HamburgerMenu.prototype, "navItems", void 0);
__decorate([
    observable
], HamburgerMenu.prototype, "selectedNavId", void 0);
__decorate([
    observable
], HamburgerMenu.prototype, "hamburgerMenuButtonTelemetryTag", void 0);
__decorate([
    observable
], HamburgerMenu.prototype, "closeButtonTelemetryTag", void 0);
